import { Box, Dialog, Typography } from '@material-ui/core'
import { verifyCorrectVersion } from 'components/App'
import { Button, Sidebar } from 'components/facets'
import { useUser } from 'components/providers/UserProvider'
import { default as Cookies } from 'js-cookie'
import { shopifyConnectShop } from 'lib/api'
import { useAppContext, useLocalStorageState } from 'lib/hooks'
import { useTransactionsSpinnerStore } from 'lib/hooks/useTransactionsSpinnerStore'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Redirect, useHistory, useLocation } from 'react-router-dom'
import { ElementWithChildren } from 'types'
import { SpinnerDrawer } from '../Spinner/SpinnerDrawer'
import { SpinnerIcon } from '../Spinner/SpinnerIcon'

const CARDMARKET_DISCONNECTED_SNACKBAR_KEY = 'cardmarket-disconnected'
const STAFF_ROLES = ['admin', 'content_editor', 'mkm_products_viewer']

export const AppLayout = ({
  children,
  noDOMLayout = false,
}: ElementWithChildren & { noDOMLayout?: boolean }): JSX.Element => {
  const { user } = useUser()
  const { isAuthenticated } = useAppContext()
  const { t } = useTranslation()
  const [bannerMessage, setBannerMessage] = useState<{ _id: string; message: string } | undefined>(
    undefined
  )

  const [ignoredBannerMessages, setIgnoredBannerMessages] = useLocalStorageState<string[]>(
    [],
    'ignoredBannerMessages'
  )

  const bannerMessageToShow = ignoredBannerMessages.includes(bannerMessage?._id ?? '')
    ? ''
    : bannerMessage?.message

  const location = useLocation()
  const history = useHistory()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const [updateAvailableDialogOpen, setUpdateAvailableDialogOpen] = useState(false)
  const { showSpinnerLoading, setShowSpinnerLoading } = useTransactionsSpinnerStore()

  // getUser clears cardmarketUsername when the Cardmarket tokens stop working but keeps the account
  // in assignedCardmarketAccounts, so this shows up on the next user fetch without polling. Users
  // who never connected Cardmarket have no assigned accounts; CAD users have no Cardmarket at all.
  // Our own staff roles (see ROLES in Rails app/models/user.rb) are left out on purpose.
  const cardmarketDisconnected =
    !STAFF_ROLES.includes(user?.role ?? '') &&
    !user?.cardmarketUsername &&
    user?.assignedCardmarketAccounts?.length > 0 &&
    user?.currency !== 'CAD'

  useEffect(() => {
    if (!cardmarketDisconnected) {
      closeSnackbar(CARDMARKET_DISCONNECTED_SNACKBAR_KEY)
      return
    }

    enqueueSnackbar(t('misc.cardmarketDisconnected'), {
      key: CARDMARKET_DISCONNECTED_SNACKBAR_KEY,
      // AppLayout remounts on every page: the same key keeps a single snackbar on screen
      preventDuplicate: true,
      persist: true,
      variant: 'error',
      action: (key) => (
        <Button
          variant="contained"
          onClick={() => {
            closeSnackbar(key)
            history.push('/account?tab=salesChannels')
          }}
        >
          {t('misc.reconnectCardmarket')}
        </Button>
      ),
    })
  }, [cardmarketDisconnected])

  useEffect(() => {
    const onFocus = () => {
      verifyCorrectVersion(() => setUpdateAvailableDialogOpen(true))
    }
    window.addEventListener('focus', onFocus)
    // Specify how to clean up after this effect:
    return () => {
      window.removeEventListener('focus', onFocus)
    }
  }, [])

  useEffect(() => {
    setShowSpinnerLoading(true)
    const prevDomain = Cookies.get('prev_domain')
    Cookies.remove('prev_domain', { domain: process.env.REACT_APP_HOST_MAIN_DOMAIN })

    if (!isAuthenticated) return

    const shopifyShop = Cookies.get('shopify_oauth')
    if (shopifyShop) {
      shopifyConnectShop(user._id, shopifyShop).finally(() => {
        Cookies.remove('shopify_oauth')
      })
    }

    if (prevDomain && prevDomain != window.location.host && prevDomain != 'new.tcgpowertools.com') {
      window.location.href = `https://${prevDomain}`
    }
    setShowSpinnerLoading(false)
  }, [])

  // if (user?.hasNotLoggedIn && !user.hasOnboarded && location.pathname !== '/finish-signup')
  //   return <Redirect to="/finish-signup" />
  // else if (location.pathname === '/finish-signup') return <Box>{children}</Box>
  // else
  if (!user?.hasOnboarded) {
    return <Redirect to="/onboarding" />
  } else if (!isAuthenticated) return <Redirect to="/signin" />
  // Force users flagged with `forceGradeSync` onto the Cardmarket sync page. These users hit the
  // `nothing_to_move` branch of the graded-stock cleanup: on Cardmarket we tried to decrement the
  // wrong-grade listing, found no match, so we did NOT add it back to the canonical — leaving a
  // possible PT<->Cardmarket quantity divergence on graded cards that the user must resolve via a sync.
  // The backend sets the flag; it is cleared once the sync completes (see SyncInventoryPresenter).
  // Guard conditions (both required to avoid an infinite redirect loop):
  //   - isTester.ptInventory: /inventory's own guard (Inventory.tsx) bounces non-testers back to '/',
  //     which would ping-pong with this redirect forever.
  //   - pathname !== '/inventory': don't redirect the page we're sending them to onto itself.
  else if (
    user.featureFlags?.forceGradeSync &&
    user.isTester?.ptInventory &&
    location.pathname !== '/inventory'
  ) {
    return <Redirect to="/inventory" />
  } else
    return (
      <>
        {showSpinnerLoading && <SpinnerIcon />}
        <SpinnerDrawer />
        {bannerMessageToShow && (
          <Box
            padding={0.5}
            width="100vw"
            height="40px"
            style={{ backgroundColor: 'orange', textAlign: 'center' }}
          >
            <Box paddingLeft={12} display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body1">
                {/* eslint-disable-next-line */}
                <span dangerouslySetInnerHTML={{ __html: bannerMessageToShow }} />
              </Typography>
              <Button
                onClick={() =>
                  setIgnoredBannerMessages([...ignoredBannerMessages, bannerMessage!._id])
                }
                color="primary"
              >
                {t('common.ok')}
              </Button>
            </Box>
          </Box>
        )}
        <div className="app-layout-container">
          <Sidebar setBannerMessage={setBannerMessage} />
          {noDOMLayout ? (
            <div className="app-layout-children w-100">{children}</div>
          ) : (
            <div className="app-layout-children w-100">{children}</div>
          )}
          {updateAvailableDialogOpen && (
            <Dialog fullWidth maxWidth="sm" open={updateAvailableDialogOpen}>
              <Box textAlign="center" padding={4}>
                <Typography variant="h4" color="primary">
                  {t('misc.weHaveUpdates')}
                </Typography>
                <Box marginTop={3} marginBottom={3}>
                  <Typography variant="h6">{t('misc.weHaveUpdatesExplainer')}</Typography>
                </Box>
                <Box width="20rem" margin="0 auto">
                  <Button
                    style={{ fontSize: '1.3rem' }}
                    variant="contained"
                    color="primary"
                    fullWidth
                    //@ts-ignore
                    onClick={() => window.location.reload(true)}
                  >
                    {t('misc.refreshToUpdate')}
                  </Button>
                  <Button onClick={() => setUpdateAvailableDialogOpen(false)} fullWidth>
                    {t('misc.willDoLater')}
                  </Button>
                </Box>
              </Box>
            </Dialog>
          )}
        </div>
      </>
    )
}
